import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import AppDatatable from '../../components/AppDatatable';
import AccessDeniedState from '../../components/AccessDeniedState';
import { createLocalId, deleteLocalItem, normalizeStatus, readLocalCollection, upsertLocalItem } from '../../utils/localData';
import { getLoggedInUser, hasModulePermission } from '../../utils/permissions';

const USER_STORAGE_KEY = 'ra_users_local';
const ROLE_STORAGE_KEY = 'ra_access_roles_local';
const LOCATION_STORAGE_KEY = 'ra_locations_local';
const USER_PERMISSION_KEYS = ['User Create', 'User'];
const MAX_LOCATION_LABELS = 2;
const PASSWORD_CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789';
const PASSWORD_LENGTH = 7;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const createInitialForm = () => ({
  _id: '',
  user_name: '',
  user_email: '',
  password: '',
  custom_otp: '',
  phone: '',
  selected_role: '',
  selected_locations: [],
  status: 'Active',
});

const SELECT_ALL_VALUE = '__all__';

const formGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: 2,
  mt: 0.5,
  alignItems: 'start',
};

export default function UserCreatePage() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(createInitialForm());
  const [validationErrors, setValidationErrors] = useState({});
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const currentUser = getLoggedInUser();
  const currentUserName = currentUser?.userName || '';
  const accessRole = currentUser?.accessRole || {};
  const canView = hasModulePermission(USER_PERMISSION_KEYS, 'View', accessRole);
  const canCreate = hasModulePermission(USER_PERMISSION_KEYS, 'Create', accessRole);
  const canEdit = hasModulePermission(USER_PERMISSION_KEYS, 'Edit', accessRole);
  const canDelete = hasModulePermission(USER_PERMISSION_KEYS, 'Delete', accessRole);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersResponse, rolesResponse, locationsResponse] = await Promise.allSettled([
        axios.get(`${baseURL}/get_users`),
        axios.get(`${baseURL}/get_access_roles`),
        axios.get(`${baseURL}/get_locations`),
      ]);

      const apiUsers = usersResponse.status === 'fulfilled' && Array.isArray(usersResponse.value?.data)
        ? usersResponse.value.data
        : [];
      const localUsers = readLocalCollection(USER_STORAGE_KEY);
      const mergedUsers = [...apiUsers];
      localUsers.forEach((user) => {
        if (!mergedUsers.some((entry) => String(entry?._id || entry?.user_email) === String(user?._id || user?.user_email))) {
          mergedUsers.push(user);
        }
      });
      setRows(mergedUsers);

      const apiRoles = rolesResponse.status === 'fulfilled' && Array.isArray(rolesResponse.value?.data)
        ? rolesResponse.value.data
        : [];
      const localRoles = readLocalCollection(ROLE_STORAGE_KEY);
      const mergedRoles = [...apiRoles];
      localRoles.forEach((role) => {
        if (!mergedRoles.some((entry) => String(entry?._id || entry?.role_name) === String(role?._id || role?.role_name))) {
          mergedRoles.push(role);
        }
      });
      setRoles(mergedRoles);

      const apiLocations = locationsResponse.status === 'fulfilled' && Array.isArray(locationsResponse.value?.data)
        ? locationsResponse.value.data
        : [];
      const localLocations = readLocalCollection(LOCATION_STORAGE_KEY);
      const mergedLocations = [...apiLocations];
      localLocations.forEach((location) => {
        if (!mergedLocations.some((entry) => String(entry?._id || entry?.location_name) === String(location?._id || location?.location_name))) {
          mergedLocations.push(location);
        }
      });
      setLocations(mergedLocations);
    } catch {
      setRows(readLocalCollection(USER_STORAGE_KEY));
      setRoles(readLocalCollection(ROLE_STORAGE_KEY));
      setLocations(readLocalCollection(LOCATION_STORAGE_KEY));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const allLocationValues = useMemo(
    () => locations.map((location) => location.location_name).filter(Boolean),
    [locations]
  );

  const resetForm = () => {
    setForm(createInitialForm());
    setValidationErrors({});
    setOpenDialog(false);
    setSubmitting(false);
  };

  const openCreate = () => {
    if (!canCreate) return;
    setForm(createInitialForm());
    setOpenDialog(true);
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    const existingLocations = Array.isArray(row?.selected_locations || row?.locations)
      ? (row.selected_locations || row.locations)
      : typeof (row?.selected_locations || row?.locations) === 'string'
        ? (row.selected_locations || row.locations).split(',').map((item) => item.trim()).filter(Boolean)
        : [];

    setForm({
      _id: row?._id || '',
      user_name: row?.user_name || '',
      user_email: row?.user_email || '',
      password: '',
      custom_otp: row?.custom_otp || row?.otp || '',
      phone: row?.phone || '',
      selected_role: row?.selected_role || '',
      selected_locations: existingLocations,
      status: row?.status || 'Active',
    });
    setValidationErrors({});
    setOpenDialog(true);
  };

  const handleChange = ({ target: { name, value } }) => {
    setForm((prev) => ({
      ...prev,
      [name]: name === 'selected_locations'
        ? (typeof value === 'string' ? value.split(',') : value)
        : value,
    }));
    setValidationErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const generatePassword = () => {
    let generated = '';

    for (let index = 0; index < PASSWORD_LENGTH; index += 1) {
      generated += PASSWORD_CHARSET[Math.floor(Math.random() * PASSWORD_CHARSET.length)];
    }

    setForm((prev) => ({
      ...prev,
      password: generated,
    }));

    setValidationErrors((prev) => {
      if (!prev.password) return prev;
      const next = { ...prev };
      delete next.password;
      return next;
    });
  };

  const handleLocationsChange = ({ target: { value } }) => {
    const normalizedValue = typeof value === 'string' ? value.split(',') : value;
    const hasSelectAll = normalizedValue.includes(SELECT_ALL_VALUE);

    const nextLocations = hasSelectAll
      ? (form.selected_locations.length === allLocationValues.length ? [] : allLocationValues)
      : normalizedValue;

    setForm((prev) => ({
      ...prev,
      selected_locations: nextLocations,
    }));

    setValidationErrors((prev) => {
      if (!prev.selected_locations) return prev;
      const next = { ...prev };
      delete next.selected_locations;
      return next;
    });
  };

  const handleSubmit = async () => {
    if ((!form._id && !canCreate) || (form._id && !canEdit)) {
      await Swal.fire({
        icon: 'warning',
        text: form._id ? 'You do not have permission to edit users.' : 'You do not have permission to create users.',
      });
      return;
    }

    const nextErrors = {};

    if (!form.user_name.trim()) nextErrors.user_name = 'User name is required.';
    if (!form.user_email.trim()) nextErrors.user_email = 'Email is required.';
    else if (!emailRegex.test(form.user_email.trim())) nextErrors.user_email = 'Enter a valid email address.';

    if (!form.password.trim() && !form._id) nextErrors.password = 'Password is required.';
    if (form.password.trim() && form.password.trim().length < 6) nextErrors.password = 'Password must be at least 6 characters.';

    if (!form.custom_otp.trim()) nextErrors.custom_otp = 'Custom OTP is required.';
    else if (!/^\d{6}$/.test(form.custom_otp.trim())) nextErrors.custom_otp = 'Custom OTP must be 6 digits.';

    if (!form.selected_role.trim()) nextErrors.selected_role = 'Role is required.';
    if (!Array.isArray(form.selected_locations) || form.selected_locations.length === 0) {
      nextErrors.selected_locations = 'Select at least one location.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setValidationErrors(nextErrors);
      Swal.fire({ icon: 'warning', text: 'Please fix the required fields before submitting.' });
      return;
    }

    const trimmedPassword = form.password.trim();
    const payload = {
      ...form,
      user_email: form.user_email.trim().toLowerCase(),
      custom_otp: form.custom_otp.trim(),
      _id: form._id || createLocalId('user'),
      ...(form._id ? { updated_by: currentUserName } : { created_by: currentUserName }),
    };

    if (!form._id || trimmedPassword) {
      payload.password = trimmedPassword;
    } else {
      delete payload.password;
    }

    setSubmitting(true);
    try {
      let response;

      if (form._id) {
        response = await axios.put(`${baseURL}/update_user/${form._id}`, payload);
      } else {
        response = await axios.post(`${baseURL}/create_user`, payload);
      }

      const result = response?.data || {};

      if (result.status_code === 1) {
        await Swal.fire({
          icon: 'success',
          text: result.message || (form._id ? 'User updated successfully' : 'User created successfully'),
        });
        await fetchData();
        resetForm();
        return;
      }

      await Swal.fire({
        icon: 'error',
        text: result.message || 'Unable to save user.',
      });
    } catch {
      upsertLocalItem(USER_STORAGE_KEY, payload);
      await fetchData();
      await Swal.fire({
        icon: 'success',
        text: form._id ? 'User updated locally.' : 'User created locally.',
      });
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete) {
      await Swal.fire({
        icon: 'warning',
        text: 'You do not have permission to delete users.',
      });
      return;
    }

    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete user?',
      text: `Remove ${row.user_name}?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#c62828',
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.delete(`${baseURL}/delete_user/${row._id}`);
    } catch {
      deleteLocalItem(USER_STORAGE_KEY, row._id);
    } finally {
      fetchData();
    }
  };

  const tableData = useMemo(() => ({
    columns: [
      { label: 'User Name', field: 'user_name' },
      { label: 'Email', field: 'user_email' },
      { label: 'Phone', field: 'phone' },
      { label: 'Access Role', field: 'selected_role' },
      { label: 'Locations', field: 'selected_locations' },
      { label: 'Custom OTP', field: 'custom_otp' },
      { label: 'Status', field: 'status' },
      { label: 'Action', field: 'action', sort: false },
    ],
    rows: rows.map((row) => ({
      user_name: row.user_name || '-',
      user_email: row.user_email || '-',
      phone: row.phone || '-',
      selected_role: row.selected_role || '-',
      selected_locations: Array.isArray(row.selected_locations || row.locations)
        ? (row.selected_locations || row.locations).join(', ')
        : '-',
      custom_otp: row.custom_otp || row.otp || '-',
      status: normalizeStatus((row.status || 'Active') === 'Active'),
      action: (
        <Stack direction="row" spacing={1}>
          {canEdit ? <Button size="small" variant="outlined" onClick={() => openEdit(row)}>Edit</Button> : null}
          {canDelete ? <Button size="small" color="error" variant="contained" onClick={() => handleDelete(row)}>Delete</Button> : null}
          {!canEdit && !canDelete ? '-' : null}
        </Stack>
      ),
    })),
  }), [canDelete, canEdit, rows]);

  if (!canView) {
    return <AccessDeniedState message="You do not have permission to view users." />;
  }

  const getLocationRenderValue = (selected) => {
    if (!selected.length) return '';
    if (selected.length === allLocationValues.length && allLocationValues.length > 0) {
      return `All Locations (${selected.length})`;
    }
    if (selected.length > MAX_LOCATION_LABELS) {
      return `${selected.slice(0, MAX_LOCATION_LABELS).join(', ')} +${selected.length - MAX_LOCATION_LABELS} more`;
    }
    return selected.join(', ');
  };

  return (
    <Box sx={{ minHeight: '100vh', ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 } }}>
      <AppDatatable
        title="Users"
        subtitle="Manage created users and the access role assigned to them."
        headerActions={canCreate ? <Button variant="contained" onClick={openCreate}>Create User</Button> : null}
        data={tableData}
        loading={loading}
        noFoundMessage="No users created yet."
      />

      <Dialog open={openDialog} onClose={resetForm} fullWidth maxWidth="md">
        <DialogTitle>{form._id ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          <Box sx={formGridSx}>
            <Box>
              <TextField
                required
                label="User Name"
                name="user_name"
                value={form.user_name}
                onChange={handleChange}
                error={Boolean(validationErrors.user_name)}
                helperText={validationErrors.user_name || ''}
              />
            </Box>
            <Box>
              <TextField
                required
                label="Email"
                name="user_email"
                type="email"
                value={form.user_email}
                onChange={handleChange}
                error={Boolean(validationErrors.user_email)}
                helperText={validationErrors.user_email || ''}
              />
            </Box>
            <Box>
              <Stack spacing={1}>
                <TextField
                  required={!form._id}
                  label={form._id ? 'Password (leave blank to keep same)' : 'Password'}
                  name="password"
                  type="text"
                  value={form.password}
                  onChange={handleChange}
                  error={Boolean(validationErrors.password)}
                  helperText={validationErrors.password || `Auto-suggest creates a ${PASSWORD_LENGTH}-character password.`}
                  inputProps={{ maxLength: PASSWORD_LENGTH }}
                />
                <Button variant="outlined" onClick={generatePassword} sx={{ alignSelf: 'flex-start' }}>
                  Auto Suggest
                </Button>
              </Stack>
            </Box>
            <Box>
              <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} />
            </Box>
            <Box>
              <TextField
                required
                label="Custom OTP"
                name="custom_otp"
                value={form.custom_otp}
                onChange={handleChange}
                error={Boolean(validationErrors.custom_otp)}
                helperText={validationErrors.custom_otp || 'Use 6 digits.'}
              />
            </Box>
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel required error={Boolean(validationErrors.selected_role)}>Access Role</InputLabel>
                <Select
                  label="Access Role"
                  name="selected_role"
                  value={form.selected_role}
                  onChange={handleChange}
                  error={Boolean(validationErrors.selected_role)}
                >
                  {roles.map((role) => (
                    <MenuItem key={role._id || role.role_name} value={role.role_name}>
                      {role.role_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {validationErrors.selected_role ? (
                <Typography variant="caption" color="error" sx={{ ml: 1.75, mt: 0.5, display: 'block' }}>
                  {validationErrors.selected_role}
                </Typography>
              ) : null}
            </Box>
            <Box>
              <FormControl fullWidth size="small" error={Boolean(validationErrors.selected_locations)}>
                <InputLabel required>Locations</InputLabel>
                <Select
                  multiple
                  name="selected_locations"
                  value={form.selected_locations}
                  onChange={handleLocationsChange}
                  input={<OutlinedInput label="Locations" />}
                  renderValue={getLocationRenderValue}
                  sx={{
                    '& .MuiSelect-select': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                  <MenuItem value={SELECT_ALL_VALUE}>
                    <Checkbox
                      checked={allLocationValues.length > 0 && form.selected_locations.length === allLocationValues.length}
                      indeterminate={form.selected_locations.length > 0 && form.selected_locations.length < allLocationValues.length}
                    />
                    <Typography>Select All</Typography>
                  </MenuItem>
                  {locations.map((location) => {
                    const locationValue = location.location_name;
                    return (
                      <MenuItem key={location._id || locationValue} value={locationValue}>
                        <Checkbox checked={form.selected_locations.indexOf(locationValue) > -1} />
                        <Typography>{locationValue}</Typography>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {validationErrors.selected_locations ? (
                <Typography variant="caption" color="error" sx={{ ml: 1.75, mt: 0.5, display: 'block' }}>
                  {validationErrors.selected_locations}
                </Typography>
              ) : null}
            </Box>
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" name="status" value={form.status} onChange={handleChange}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={resetForm}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
